import React, { useState, useEffect } from 'react';
import {
  Dialog,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  alpha,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Close,
  Delete
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import { postApi } from '../../services/postApi';

// Import your logo (assuming you have a text version)
import logo from '../../assets/Text-WFHCafe_WObg.png';

interface EditPostModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (updatedPost: any) => void;
  post: {
    id: string;
    title: string;
    content: string;
    type: 'text' | 'image' | 'video';
    mediaUrl?: string;
    mediaType?: 'image' | 'video';
    tags: string[];
  };
}

const EditPostModal: React.FC<EditPostModalProps> = ({
  open,
  onClose,
  onSuccess,
  post
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useCustomTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldGradient = `linear-gradient(135deg, ${goldColor} 0%, #D4C690 100%)`;

  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: isMobile ? 'calc(100vw - 32px)' : (isTablet ? '600px' : '700px'),
    maxHeight: isMobile ? 'calc(100vh - 300px)' : '80vh',
    margin: 'auto',
    overflow: 'hidden',
    padding: 0,
    '& .MuiDialogContent-root': {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    },
  };

  // Input field styles with gold focus border
  const inputFieldStyle = {
    borderRadius: '12px',
    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.1)',
        borderWidth: '1px',
        borderRadius: '12px'
      },
      '&:hover fieldset': { 
        borderColor: goldColor,
        borderWidth: '1px'
      },
      '&.Mui-focused fieldset': { 
        borderColor: goldColor,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
      fontWeight: 600
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: goldColor,
      fontWeight: 700
    }
  };

  const gradientButtonStyle = {
    background: isDark ? goldGradient : lokiGradient,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '50px',
    fontWeight: 800,
    textTransform: 'none',
    fontSize: isMobile ? '0.9rem' : '1rem',
    padding: isMobile ? '10px 20px' : '12px 32px',
    letterSpacing: '-0.02em',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 8px 32px ${alpha(goldColor, 0.25)}`
      : `0 8px 32px ${alpha(deepGreen, 0.2)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: isDark
        ? `0 12px 40px ${alpha(goldColor, 0.35)}`
        : `0 12px 40px ${alpha(deepGreen, 0.3)}`,
      background: isDark
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, #E5D9A8 100%)`
        : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    }
  };

  // Initialize form data when post changes
  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title,
        content: post.content || '',
        tags: post.tags.join(', ')
      });
      setTags(post.tags);
    }
  }, [post]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const newTags = [...tags, trimmedTag];
      setTags(newTags);
      setFormData(prev => ({
        ...prev,
        tags: newTags.join(', ')
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    setTags(newTags);
    setFormData(prev => ({
      ...prev,
      tags: newTags.join(', ')
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (formData.content.length > 1500) {
      toast.error('Content must be 1500 characters or less');
      return;
    }

    setLoading(true);

    try {
      const response = await postApi.updatePost(post.id, {
        title: formData.title,
        content: formData.content,
        tags: tags.join(',')
      });

      if (response.success) {
        toast.success('Post updated successfully!');
        onSuccess(response.post);
        handleClose();
      } else {
        toast.error(response.error || 'Failed to update post');
      }
    } catch (error: any) {
      console.error('Update post error:', error);
      toast.error(error.message || 'Failed to update post');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      tags: ''
    });
    setTags([]);
    setTagInput('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
    >
      <Box sx={{
        p: isMobile ? 3 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Logo and Close Button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 3 : 4,
        }}>
          <Box
            component="img"
            src={logo}
            alt="WFHCafe Logo"
            sx={{
              height: isMobile ? 35 : 45,
              width: 'auto',
              filter: isDark ? 'brightness(1.1)' : 'none',
            }}
          />
          
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              padding: isMobile ? '6px' : '8px',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "medium" : "large"} />
          </IconButton>
        </Box>

        {/* Title */}
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          sx={{ 
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: isDark ? '#fff' : deepGreen,
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2rem',
          }}
        >
          Edit Post
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
            mb: isMobile ? 3 : 4,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}
        >
          Update your post information
        </Typography>

        {/* Title Input */}
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          sx={{ mb: 3, ...inputFieldStyle }}
          placeholder="Give your post a title..."
        />

        {/* Content Input */}
        <TextField
          fullWidth
          label="Content"
          name="content"
          value={formData.content}
          onChange={handleInputChange}
          multiline
          rows={isMobile ? 5 : 6}
          sx={{ mb: 3, ...inputFieldStyle }}
          placeholder="What do you want to share?"
          helperText={`${formData.content.length}/1500 characters`}
          FormHelperTextProps={{
            sx: {
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
            }
          }}
        />

        {/* Tags Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ 
            mb: 1, 
            fontWeight: 700,
            color: isDark ? '#fff' : deepGreen,
            fontSize: isMobile ? '0.85rem' : '0.95rem',
          }}>
            Tags
          </Typography>
          
          {/* Current Tags */}
          {tags.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mb: 2 
            }}>
              {tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={`#${tag}`}
                  onDelete={() => handleRemoveTag(tag)}
                  sx={{
                    bgcolor: alpha(goldColor, 0.1),
                    color: goldColor,
                    fontWeight: 600,
                    borderRadius: '10px',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    '& .MuiChip-deleteIcon': {
                      color: goldColor,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      '&:hover': {
                        color: alpha(goldColor, 0.7),
                      }
                    }
                  }}
                />
              ))}
            </Box>
          )}
          
          {/* Add Tag Input */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Add tags"
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyPress={handleKeyPress}
              sx={{ 
                ...inputFieldStyle,
                '& .MuiOutlinedInput-root': {
                  ...inputFieldStyle['& .MuiOutlinedInput-root'],
                  '& fieldset': {
                    borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.1)',
                  }
                }
              }}
              placeholder="Type a tag and press Enter or click Add"
              size="small"
            />
            <Button
              variant="outlined"
              onClick={handleAddTag}
              disabled={!tagInput.trim()}
              sx={{
                color: goldColor,
                borderColor: alpha(goldColor, 0.3),
                fontWeight: 700,
                borderRadius: '12px',
                minWidth: '80px',
                height: '40px',
                '&:hover': {
                  borderColor: goldColor,
                  bgcolor: alpha(goldColor, 0.1)
                },
                '&:disabled': {
                  color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(5,24,20,0.3)',
                  borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                }
              }}
            >
              Add
            </Button>
          </Box>
          <Typography variant="caption" sx={{ 
            mt: 1,
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
            fontSize: isMobile ? '0.75rem' : '0.85rem',
            display: 'block'
          }}>
            Separate tags with commas when typing in the main tags field
          </Typography>
        </Box>

        {/* Media Preview (if post has media) */}
        {post.type !== 'text' && post.mediaUrl && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              fontSize: isMobile ? '0.85rem' : '0.95rem',
            }}>
              Current Media
            </Typography>
            <Box sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              border: `1px solid ${alpha(goldColor, 0.2)}`,
              maxHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {post.type === 'image' ? (
                <img
                  src={post.mediaUrl}
                  alt="Post media"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <video
                  src={post.mediaUrl}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                  controls
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ 
              mt: 1,
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
              fontSize: isMobile ? '0.75rem' : '0.85rem',
              display: 'block',
              fontStyle: 'italic'
            }}>
              Note: Media files cannot be changed after posting
            </Typography>
          </Box>
        )}

        {/* Buttons at Bottom */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 'auto',
          pt: 3,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 2 : 0,
        }}>
          <Button
            onClick={handleClose}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontWeight: 700,
              borderRadius: '50px',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? '0.9rem' : '1rem',
              textTransform: 'none',
              minWidth: isMobile ? '120px' : 'auto',
              '&:hover': {
                bgcolor: alpha(goldColor, 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              ...gradientButtonStyle,
              minWidth: isMobile ? '120px' : 'auto',
              '&:disabled': {
                background: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                color: isDark ? 'rgba(5, 24, 20, 0.5)' : 'rgba(196, 182, 111, 0.5)',
                transform: 'none',
                boxShadow: 'none',
              }
            }}
          >
            {loading ? 'Updating...' : 'Update Post'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditPostModal;